import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { track } from "../utils/track";
import {
    ArrowForward, EmojiEvents, Extension, GpsFixed, Groups, Portrait,
    StackedBarChart, Today, TrendingUp
} from "@mui/icons-material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import styles from "./GamesHubPage.module.css";

type MuiIcon = OverridableComponent<SvgIconTypeMap<{}, "svg">>;

interface GameCard {
    key: string;
    link: string;
    icon: MuiIcon;
    iconBg: string;
    iconColor: string;
}

// PowerStrikle gets the daily-hero treatment; the rest sit in the grid (Games.dc.html)
const GAMES: GameCard[] = [
    { key: "tierlist", link: "/tierlist", icon: StackedBarChart, iconBg: "rgba(241,196,15,0.15)", iconColor: "#f1c40f" },
    { key: "whosThatPro", link: "/whos-that-pro", icon: Portrait, iconBg: "rgba(74,163,255,0.15)", iconColor: "#4aa3ff" },
    { key: "ratingDuel", link: "/rating-duel", icon: TrendingUp, iconBg: "rgba(39,174,96,0.15)", iconColor: "#4ade80" },
    { key: "logoPuzzle", link: "/logo-puzzle", icon: Extension, iconBg: "rgba(168,85,247,0.15)", iconColor: "#c084fc" },
    { key: "teamRoster", link: "/team-roster", icon: Groups, iconBg: "rgba(23,162,184,0.16)", iconColor: "#4ecbe0" },
];

const GamesHubPage: React.FC = () => {
    const { t } = useTranslation("pages");

    useEffect(() => { track("games-hub-open"); }, []);

    return (
        <div className={styles.page}>
            {/* ===== Header ===== */}
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>{t('gamesHub.title')}</h1>
                    <p className={styles.subtitle}>{t('gamesHub.subtitle')}</p>
                </div>
                <Link to="/games/leaderboard" className={styles.leaderboardLink}>
                    <EmojiEvents sx={{ fontSize: 19 }} />
                    {t('gamesLeaderboard.panelTitle')}
                    <ArrowForward sx={{ fontSize: 17 }} />
                </Link>
            </div>

            {/* ===== Daily hero: PowerStrikle ===== */}
            <Link to="/powerstrikle" className={styles.hero}>
                <span className={styles.heroIcon}>
                    <GpsFixed sx={{ fontSize: 44 }} />
                </span>
                <div className={styles.heroBody}>
                    <span className={styles.heroPill}>
                        <Today sx={{ fontSize: 13 }} />
                        {t('gamesHub.dailyPill')}
                    </span>
                    <div className={styles.heroTitle}>PowerStrikle</div>
                    <p className={styles.heroDesc}>{t('gamesHub.dailyDesc')}</p>
                </div>
                <span className={styles.heroButton}>
                    {t('gamesHub.playToday')}
                    <ArrowForward sx={{ fontSize: 19 }} />
                </span>
            </Link>

            {/* ===== Game grid ===== */}
            <div className={styles.grid}>
                {GAMES.map((g) => {
                    const Icon = g.icon;
                    const title = t(`gamesHub.games.${g.key}.title`);
                    const desc = t(`gamesHub.games.${g.key}.desc`);

                    const inner = (
                        <>
                            <div className={styles.cardTop}>
                                <span className={styles.cardIcon} style={{ background: g.iconBg, color: g.iconColor }}>
                                    <Icon sx={{ fontSize: 27 }} />
                                </span>
                            </div>
                            <div className={styles.cardTitle}>{title}</div>
                            <p className={styles.cardDesc}>{desc}</p>
                            <span className={`${styles.cardFooter} ${styles.footerPlay}`}>
                                {t('gamesHub.play')}
                                <ArrowForward sx={{ fontSize: 18 }} />
                            </span>
                        </>
                    );

                    return (
                        <Link key={g.key} to={g.link} className={styles.card}>
                            {inner}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default GamesHubPage;
